import { useCallback, useState } from 'react'
import { useTranslation } from 'react-i18next'

import { useToast } from '@percent/lemonade'
import { useServices } from '@percent/partner-dashboard/context/serviceContext/ServiceContext'

export function useCopyAttendeesEmails() {
  const { t } = useTranslation()
  const { workplaceGivingService } = useServices()
  const { addToast } = useToast()
  const [isCopying, setIsCopying] = useState(false)

  const copyEmails = useCallback(
    async (opportunityId: string) => {
      if (isCopying) return

      setIsCopying(true)

      let emailCount = 0
      let noEmails = false

      try {
        const fetchAllEmails = async (): Promise<Set<string>> => {
          const emails = new Set<string>()

          const fetchPage = async (nextUrl?: string): Promise<void> => {
            const response = await workplaceGivingService.getOpportunityParticipants(opportunityId, nextUrl)

            response.data.data.forEach(participant => {
              if (participant.notificationEmails?.length) {
                participant.notificationEmails.forEach(email => emails.add(email))
              } else if (participant.email) {
                emails.add(participant.email)
              }
            })

            // eslint-disable-next-line no-underscore-dangle
            const { next } = response.data._links

            if (next) {
              return fetchPage(next)
            }

            return undefined
          }

          await fetchPage()

          return emails
        }

        // Create ClipboardItem synchronously within user gesture so Safari
        // doesn't revoke clipboard access while we await the API response.
        const clipboardItem = new ClipboardItem({
          'text/plain': fetchAllEmails().then(emails => {
            emailCount = emails.size
            noEmails = emails.size === 0

            return new Blob([[...emails].join('\n')], { type: 'text/plain' })
          })
        })

        await navigator.clipboard.write([clipboardItem])

        if (noEmails) {
          addToast(t('toast.volunteering.copyEmails.noEmails'), 'success')
        } else {
          addToast(t('toast.volunteering.copyEmails.success', { count: emailCount }), 'success')
        }
      } catch {
        addToast(t('toast.volunteering.copyEmails.error'), 'error')
      } finally {
        setIsCopying(false)
      }
    },
    [isCopying, workplaceGivingService, addToast, t]
  )

  return { copyEmails, isCopying }
}
